import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { fetchFeaturedArtCategoryArtists, fetchJudaicaCategoryArtists } from '../../ducks/artistUsernames.duck';

// ================ Action types ================ //

export const FETCH_FEATURED_LISTINGS_REQUEST = 'app/LandingPage/FETCH_FEATURED_LISTINGS_REQUEST';
export const FETCH_FEATURED_LISTINGS_SUCCESS = 'app/LandingPage/FETCH_FEATURED_LISTINGS_SUCCESS';
export const FETCH_FEATURED_LISTINGS_ERROR = 'app/LandingPage/FETCH_FEATURED_LISTINGS_ERROR';

export const FETCH_FEATURED_JUDAICA_LISTINGS_REQUEST = 'app/LandingPage/FETCH_FEATURED_JUDAICA_LISTINGS_REQUEST';
export const FETCH_FEATURED_JUDAICA_LISTINGS_SUCCESS = 'app/LandingPage/FETCH_FEATURED_JUDAICA_LISTINGS_SUCCESS';
export const FETCH_FEATURED_JUDAICA_LISTINGS_ERROR = 'app/LandingPage/FETCH_FEATURED_JUDAICA_LISTINGS_ERROR';

export const LANDING_PAGE_ASSET = 'landing-page-new';

// ================ Reducer ================ //

const initialState = {
  featuredListingIds: [],
  featuredListingsInProgress: false,
  featuredListingsError: null,
  featuredJudaicaListingIds: [],
  featuredJudaicaListingsInProgress: false,
  featuredJudaicaListingsError: null,
  pageAssetsData: null,
  pageAssetsInProgress: false,
  pageAssetsError: null,
};

// Helper function to extract listing IDs from response data
const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

const landingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_FEATURED_LISTINGS_REQUEST:
      return {
        ...state,
        featuredListingsInProgress: true,
        featuredListingsError: null,
      };
    case FETCH_FEATURED_LISTINGS_SUCCESS:
      return {
        ...state,
        featuredListingIds: resultIds(payload.data),
        featuredListingsInProgress: false,
      };
    case FETCH_FEATURED_LISTINGS_ERROR:
      return {
        ...state,
        featuredListingsInProgress: false,
        featuredListingsError: payload,
      };
    case FETCH_FEATURED_JUDAICA_LISTINGS_REQUEST:
      return {
        ...state,
        featuredJudaicaListingsInProgress: true,
        featuredJudaicaListingsError: null,
      };
    case FETCH_FEATURED_JUDAICA_LISTINGS_SUCCESS:
      return {
        ...state,
        featuredJudaicaListingIds: resultIds(payload.data),
        featuredJudaicaListingsInProgress: false,
      };
    case FETCH_FEATURED_JUDAICA_LISTINGS_ERROR:
      return {
        ...state,
        featuredJudaicaListingsInProgress: false,
        featuredJudaicaListingsError: payload,
      };
    default:
      return state;
  }
};

export default landingPageReducer;

// ================ Action creators ================ //

export const fetchFeaturedListingsRequest = () => ({ type: FETCH_FEATURED_LISTINGS_REQUEST });
export const fetchFeaturedListingsSuccess = response => ({
  type: FETCH_FEATURED_LISTINGS_SUCCESS,
  payload: { data: response.data },
});
export const fetchFeaturedListingsError = error => ({
  type: FETCH_FEATURED_LISTINGS_ERROR,
  payload: error,
});

export const fetchFeaturedJudaicaListingsRequest = () => ({ type: FETCH_FEATURED_JUDAICA_LISTINGS_REQUEST });
export const fetchFeaturedJudaicaListingsSuccess = response => ({
  type: FETCH_FEATURED_JUDAICA_LISTINGS_SUCCESS,
  payload: { data: response.data },
});
export const fetchFeaturedJudaicaListingsError = error => ({
  type: FETCH_FEATURED_JUDAICA_LISTINGS_ERROR,
  payload: error,
});

export const fetchFeaturedListings = (config) => (dispatch, getState, sdk) => {
  dispatch(fetchFeaturedListingsRequest());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    // Filter for featured art listings
    meta_isFeatured: true,
    pub_listingType: 'art',
    // Only published and non-deleted listings
    state: 'published',
    // Include necessary data
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'metadata.likesCount',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.priceVariationsEnabled',
      'publicData.priceVariants',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
    pub_isVariant: false,
    sort: 'meta_rating',
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchFeaturedListingsSuccess(response));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(fetchFeaturedListingsError(error));
      throw e;
    });
};

export const fetchFeaturedJudaicaListings = (config) => (dispatch, getState, sdk) => {
  dispatch(fetchFeaturedJudaicaListingsRequest());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    // Filter for featured judaica listings
    meta_isFeatured: true,
    pub_listingType: 'judaica',
    // Only published and non-deleted listings
    state: 'published',
    // Include necessary data
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'metadata.likesCount',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.priceVariationsEnabled',
      'publicData.priceVariants',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
    pub_isVariant: false,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchFeaturedJudaicaListingsSuccess(response));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(fetchFeaturedJudaicaListingsError(error));
      throw e;
    });
};

export const loadData = (search, history, config) => (dispatch, getState, sdk) => {
  // Fetch page assets
  const pageAsset = { landingPage: `content/pages/${LANDING_PAGE_ASSET}.json` };
  const pageAssetsPromise = dispatch(fetchPageAssets(pageAsset, true));
  
  // Fetch featured listings
  const featuredListingsPromise = dispatch(fetchFeaturedListings(config));
  
  // Fetch featured judaica listings
  const featuredJudaicaListingsPromise = dispatch(fetchFeaturedJudaicaListings(config));

  // Fetch category-specific artists
  const featuredArtCategoryArtistsPromise = dispatch(fetchFeaturedArtCategoryArtists());
  const judaicaCategoryArtistsPromise = dispatch(fetchJudaicaCategoryArtists());

  return Promise.all([
    pageAssetsPromise, 
    featuredListingsPromise, 
    featuredJudaicaListingsPromise,
    featuredArtCategoryArtistsPromise,
    judaicaCategoryArtistsPromise
  ]);
}; 